/* ============================================================================
 * AccountExportCard — Settings › Account (owner only).
 *
 * "Export everything" (terms §9): free, any time, including read-only. The
 * account-export edge function builds it in the background and emails links
 * that work for 7 days; the same links show here. Migration 111.
 *
 * ExportPanel is shared with the Delete-now dialog, which offers the export
 * once before deleting.
 * ========================================================================== */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download, Loader2, FileArchive } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { exportBusy, requestExport } from '@/lib/accountData';

const fmtDate = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const fmtSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

export const ExportPanel = ({ exp, setExp, compact = false }) => {
  const { t, i18n } = useTranslation();
  const [starting, setStarting] = useState(false);
  const busy = exportBusy(exp);
  const ready = exp?.status === 'ready' && Array.isArray(exp.parts) && exp.parts.length > 0;

  const start = async () => {
    setStarting(true);
    try {
      const next = await requestExport();
      setExp?.(next);
      toast.success(t('account.export_started_toast'));
    } catch (e) {
      toast.error(t(`account.err_${e.code}`, { defaultValue: t('account.err_generic') }));
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="account-export-panel">
      {busy ? (
        <div className="flex items-start gap-2 rounded-md border border-border p-3 text-sm" role="status">
          <Loader2 className="h-4 w-4 mt-0.5 shrink-0 animate-spin text-[hsl(var(--primary))]" />
          <div className="min-w-0">
            <div className="font-medium text-foreground">{t('account.export_building')}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{t('account.export_building_help')}</div>
          </div>
        </div>
      ) : null}

      {ready ? (
        <div className="rounded-md border border-border p-3 space-y-2">
          <div className="text-sm font-medium text-foreground">
            {t('account.export_ready', { date: fmtDate(exp.expires_at, i18n.language) })}
          </div>
          <ul className="space-y-1.5">
            {exp.parts.map((p, i) => (
              <li key={p.url || i}>
                <a
                  href={p.url}
                  className="flex items-center gap-2 text-sm text-[hsl(var(--primary))] underline underline-offset-2 break-words"
                  data-testid="account-export-link"
                >
                  <FileArchive className="h-4 w-4 shrink-0" />
                  <span className="min-w-0">
                    {i === 0 ? t('account.export_part_records') : t('account.export_part_files', { n: i, total: exp.parts.length - 1 })}
                  </span>
                  <span className="text-xs text-muted-foreground no-underline whitespace-nowrap">{fmtSize(p.bytes)}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {exp?.status === 'failed' ? (
        <p className="text-sm text-[hsl(var(--destructive))]">{t('account.export_failed')}</p>
      ) : null}

      {!busy ? (
        <Button
          variant={ready ? 'outline' : 'default'}
          size={compact ? 'sm' : 'default'}
          onClick={start}
          disabled={starting}
          data-testid="account-export-btn"
        >
          {starting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
          {ready ? t('account.export_again_btn') : t('account.export_btn')}
        </Button>
      ) : null}
    </div>
  );
};

const AccountExportCard = ({ exp, setExp }) => {
  const { t } = useTranslation();
  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="account-export-card">
      <CardContent className="p-4 sm:p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 flex items-center justify-center shrink-0">
            <Download size={16} className="text-[hsl(var(--primary))]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {t('account.export_eyebrow')}
            </div>
            <div className="text-base font-semibold text-foreground tracking-tight">
              {t('account.export_title')}
            </div>
          </div>
        </div>
        <p className="text-sm text-foreground">{t('account.export_body')}</p>
        <ExportPanel exp={exp} setExp={setExp} />
      </CardContent>
    </Card>
  );
};

export default AccountExportCard;
